import { PostHogProvider } from '@posthog/react'
import * as Sentry from '@sentry/react'
import type { User } from '@supabase/supabase-js'
import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
} from '@tanstack/react-router'
import { Toaster } from 'sonner'

import { ThemeProvider } from '#components/layout/theme-provider'
import { posthog } from '#lib/posthog'

export interface MyRouterContext {
  queryClient: QueryClient
  auth: {
    user: User | null
    email: string | null
    name: string | null
    role: string | null
    tenantId: string | null
    isLoading: boolean
    isActive: boolean
  }
}

const RootLayout = () => (
  <PostHogProvider client={posthog}>
    <HeadContent />
    <ThemeProvider defaultTheme="dark" storageKey="@usecomanda:ui-theme">
      <Outlet />
    </ThemeProvider>
    {/* <TanStackRouterDevtools /> */}
    <Toaster richColors closeButton position="top-center" />
  </PostHogProvider>
)

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      {
        title: 'UseComanda',
      },
      {
        name: 'description',
        content: 'Sistema inteligente para gestão de mesas e comandas.',
      },
    ],
  }),
  component: RootLayout,
  errorComponent: ({ error, reset }) => {
    Sentry.captureException(error)
    return (
      <div className="grid min-h-svh place-items-center p-6 text-center">
        <div>
          <h1 className="text-2xl font-semibold">Erro inesperado</h1>
          <p className="text-muted-foreground mt-2">
            {error.message || 'Não foi possível abrir esta página.'}
          </p>
          <button
            className="bg-primary text-primary-foreground mt-5 rounded-lg px-4 py-2 text-sm"
            onClick={reset}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  },
  notFoundComponent: () => (
    <div className="grid min-h-svh place-items-center p-6 text-center">
      <div>
        <h1 className="text-2xl font-semibold">Página não encontrada</h1>
        <p className="text-muted-foreground mt-2">
          O endereço informado não existe.
        </p>
        <a className="text-primary mt-4 inline-block hover:underline" href="/">
          Voltar ao início
        </a>
      </div>
    </div>
  ),
})
